// Width, in CSS pixels, the image is rendered at when zoomed. Also drives the `sizes`
// hint so the browser fetches a high-resolution source from the existing srcset rather
// than upscaling the fitted one.
const PRODUCT_MODAL_ZOOM_WIDTH = 2400;

if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.content = this.querySelector('[role="document"]');
        this.zoomed = false;
        this.onKeyDown = this.onKeyDown.bind(this);
        this.positionNumber = this.positionNumber.bind(this);
        this.bindStepButtons();
        this.bindZoomAndPan();

        // The photo-number badge is placed against the active image's measured box, and
        // that box is not settled when the image is first made active: it is fitted with
        // viewport-relative caps, so it resizes again when the full-size source decodes,
        // when the window changes, and when zoom toggles. Measuring once put the badge
        // where the image used to be. Watch the box instead of guessing when it is final.
        this.mediaResizeObserver = new ResizeObserver(this.positionNumber);
      }

      get sectionId() {
        return this.id.replace('ProductModal-', '');
      }

      // The gallery for this same section — the ids are minted together in main-product.liquid.
      get gallery() {
        return document.getElementById(`MediaGallery-${this.sectionId}`);
      }

      get activeMediaId() {
        return this.content.querySelector('[data-media-id].active')?.dataset.mediaId;
      }

      // Media ids this modal can reach, in order.
      //
      // Prefer the gallery's navigable set so the lightbox and the page agree on what
      // exists and in what order — it is derived from the thumbnail strip and already
      // excludes hidden variant duplicates. Gallery ids are `${sectionId}-${mediaId}`
      // while the modal keys off the bare media id, hence the trailing-segment split.
      //
      // Falls back to this modal's own children when there is no gallery, or when the two
      // disagree about variant media: product-media-modal.liquid filters on `media.src` OR
      // `media.id`, while product-media-gallery.liquid only checks `.src`.
      navigableMediaIds() {
        const gallery = this.gallery;
        if (gallery && typeof gallery.navigableMediaIds === 'function') {
          const ids = gallery
            .navigableMediaIds()
            .map((id) => id.slice(id.lastIndexOf('-') + 1))
            .filter((id) => this.content.querySelector(`[data-media-id="${id}"]`));
          if (ids.length > 1) return ids;
        }
        return this.ownMediaIds();
      }

      // Visibility is not usable here: only the active item is displayed, so every other
      // item would read as "hidden". Variant duplicates are excluded by class instead,
      // with the same first-child exception the stylesheet makes.
      ownMediaIds() {
        return Array.from(this.content.children)
          .filter((element) => element.hasAttribute('data-media-id'))
          .filter((element, index) => index === 0 || !element.classList.contains('product__media-item--variant'))
          .map((element) => element.dataset.mediaId);
      }

      bindStepButtons() {
        this.querySelectorAll('[data-modal-step]').forEach((button) => {
          // ModalDialog hides any `.media-modal` on mouse pointerup (global.js), which would
          // close the lightbox the instant an arrow is clicked. Stop the event here so it
          // never reaches that listener on the host element.
          button.addEventListener('pointerup', (event) => event.stopPropagation());
          button.addEventListener('click', (event) => {
            event.stopPropagation();
            this.step(button.dataset.modalStep);
          });
        });
      }

      step(direction) {
        const mediaIds = this.navigableMediaIds();
        if (mediaIds.length < 2) return;

        const currentIndex = mediaIds.indexOf(this.activeMediaId);
        if (currentIndex === -1) {
          this.setActiveMedia(mediaIds[0]);
          return;
        }

        const offset = direction === 'next' ? 1 : -1;
        this.setActiveMedia(mediaIds[(currentIndex + offset + mediaIds.length) % mediaIds.length]);
      }

      setActiveMedia(mediaId) {
        const activeMedia = this.content.querySelector(`[data-media-id="${mediaId}"]`);
        if (!activeMedia) return;

        this.resetZoom();
        this.content.querySelectorAll('[data-media-id]').forEach((element) => element.classList.remove('active'));
        activeMedia.classList.add('active');
        this.eagerLoad(activeMedia);
        this.updateCounter(mediaId);

        this.mediaResizeObserver.disconnect();
        this.mediaResizeObserver.observe(activeMedia);
        this.positionNumber();

        const template = activeMedia.querySelector('template');
        if (activeMedia.nodeName === 'DEFERRED-MEDIA' && template?.content?.querySelector('.js-youtube')) {
          activeMedia.loadContent();
        }

        // Keep the page's gallery on the same image, so closing the lightbox leaves the
        // shopper on what they were looking at instead of snapping back.
        const gallery = this.gallery;
        if (gallery && typeof gallery.setActiveMedia === 'function') {
          const galleryMediaId = `${this.sectionId}-${mediaId}`;
          if (gallery.querySelector(`[data-media-id="${galleryMediaId}"]`)) {
            gallery.setActiveMedia(galleryMediaId, false);
          }
        }
      }

      // Fitting the image to the viewport means sizing it from its intrinsic dimensions
      // (width:auto), so the HTML width/height presentation hints no longer give it a box
      // before it loads. A lazy image with a zero-size box never satisfies the viewport
      // check that would trigger its own load, so it would stay blank forever. Promote the
      // active image to an eager load to break that deadlock.
      eagerLoad(activeMedia) {
        const image = activeMedia.matches('img') ? activeMedia : activeMedia.querySelector('img');
        if (image && image.loading === 'lazy') image.loading = 'eager';
      }

      // Park the photo number on the active image's bottom-right corner.
      //
      // The gallery and the thumbnails can nest their badge inside the media container and
      // let CSS do this. Here the image has no wrapper to nest in — the stylesheet fits it
      // with `.product-media-modal__content > img.active` and the zoom/pan handlers match
      // on `img[data-media-id].active` as a direct child — so the badge lives beside the
      // image and is placed against its measured box instead.
      positionNumber() {
        const badge = this.querySelector('.media-number--modal');
        if (!badge) return;

        const active = this.content.querySelector('[data-media-id].active');
        const number = active?.dataset.mediaNumber;

        // Zoomed, the image runs well past the viewport and its bottom-right corner is
        // somewhere off screen, so a badge pinned to it would simply disappear. One photo
        // needs no number at all. Either way the "4 / 22" counter still says where you are.
        if (!number || this.zoomed || this.navigableMediaIds().length < 2) {
          badge.hidden = true;
          return;
        }

        const box = active.getBoundingClientRect();
        // A lazy image that has not decoded yet has no box. The observer will call back
        // when it gets one.
        if (box.width === 0) {
          badge.hidden = true;
          return;
        }

        badge.textContent = number;
        badge.hidden = false;
        badge.style.left = `${box.right}px`;
        badge.style.top = `${box.bottom}px`;
      }

      updateCounter(mediaId) {
        const current = this.querySelector('[data-modal-counter-current]');
        const total = this.querySelector('[data-modal-counter-total]');
        if (!current || !total) return;

        const mediaIds = this.navigableMediaIds();
        total.textContent = mediaIds.length;
        current.textContent = mediaIds.indexOf(mediaId) + 1;
      }

      bindZoomAndPan() {
        let pointerIsDown = false;
        let dragged = false;
        let startX = 0;
        let startY = 0;
        let startScrollLeft = 0;
        let startScrollTop = 0;

        this.content.addEventListener('pointerdown', (event) => {
          if (!event.target.closest('img[data-media-id].active')) return;
          pointerIsDown = true;
          dragged = false;
          startX = event.clientX;
          startY = event.clientY;
          startScrollLeft = this.content.scrollLeft;
          startScrollTop = this.content.scrollTop;
        });

        this.content.addEventListener('pointermove', (event) => {
          if (!pointerIsDown) return;
          const deltaX = event.clientX - startX;
          const deltaY = event.clientY - startY;
          if (Math.abs(deltaX) > 4 || Math.abs(deltaY) > 4) dragged = true;
          if (!this.zoomed) return;
          this.content.scrollLeft = startScrollLeft - deltaX;
          this.content.scrollTop = startScrollTop - deltaY;
        });

        this.content.addEventListener('pointerup', (event) => {
          const image = event.target.closest('img[data-media-id].active');
          if (!image) return;
          // Same reason as the arrows: stop ModalDialog's close-on-pointerup listener.
          // A click on the backdrop still reaches it and closes the lightbox.
          event.stopPropagation();
          if (pointerIsDown && !dragged) this.toggleZoom(image, event);
          pointerIsDown = false;
        });
      }

      toggleZoom(image, event) {
        if (this.zoomed) {
          this.resetZoom();
          return;
        }

        // Ask for a bigger source before magnifying, or zooming just upscales the fitted image.
        image.sizes = `${PRODUCT_MODAL_ZOOM_WIDTH}px`;
        this.zoomed = true;
        this.classList.add('is-zoomed');
        this.positionNumber();

        // Keep the point that was clicked roughly under the cursor.
        const rect = image.getBoundingClientRect();
        const ratioX = (event.clientX - rect.left) / rect.width;
        const ratioY = (event.clientY - rect.top) / rect.height;
        requestAnimationFrame(() => {
          this.content.scrollLeft = ratioX * this.content.scrollWidth - this.content.clientWidth / 2;
          this.content.scrollTop = ratioY * this.content.scrollHeight - this.content.clientHeight / 2;
        });
      }

      resetZoom() {
        if (!this.zoomed) return;
        this.zoomed = false;
        this.classList.remove('is-zoomed');
        this.content.scrollLeft = 0;
        this.content.scrollTop = 0;
        this.positionNumber();
      }

      onKeyDown(event) {
        if (!this.hasAttribute('open')) return;
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.step('next');
        } else if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.step('prev');
        }
      }

      show(opener) {
        super.show(opener);
        this.setActiveMedia(opener.getAttribute('data-media-id'));
        document.addEventListener('keydown', this.onKeyDown);
      }

      hide() {
        document.removeEventListener('keydown', this.onKeyDown);
        this.resetZoom();
        super.hide();
      }
    }
  );
}
